import { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { forgotPassword } from "../../services/Authentication";
import FormComponent from "../forms";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2 } from "lucide-react";
import queriLogo from "../../assets/logo.png";

const ForgotPasswordPage = () => {
  const { t } = useI18n();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (formData: Record<string, any>) => {
    if (loading) return { success: true };

    setLoading(true);
    const response = await forgotPassword(formData.email);

    if (!response.success) {
      setLoading(false);
      return { success: false, errors: response.errors };
    }

    setSubmitted(true);
    return { success: true };
  };

  if (submitted) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="w-full max-w-md space-y-6">
          <div className="flex flex-col items-center gap-1">
            <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
            <h1 className="text-2xl font-bold">{t.auth.forgotPassword.submitted.title}</h1>
            <p className="text-sm text-muted-foreground text-center">
              {t.auth.forgotPassword.submitted.subtitle}
            </p>
          </div>
          <Alert>
            <AlertDescription>
              {t.auth.forgotPassword.submitted.expiryNote}
            </AlertDescription>
          </Alert>
          <div className="text-center">
            <Link to="/" className="text-sm text-primary hover:underline">
              {t.auth.forgotPassword.backToLogin}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center h-full">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-1">
          <img
            src={queriLogo}
            alt="Queri"
            className="h-10 max-w-[240px] object-contain mb-3"
          />
          <h1 className="text-2xl font-bold">{t.auth.forgotPassword.title}</h1>
          <p className="text-sm text-muted-foreground">
            {t.auth.forgotPassword.subtitle}
          </p>
        </div>
        <FormComponent onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t.auth.forgotPassword.emailLabel}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder={t.auth.forgotPassword.emailPlaceholder}
              required
            />
          </div>
          {/* The form adds the spinner while submitting */}
          <Button type="submit" className="w-full mt-2" disabled={loading}>
            {loading ? t.auth.forgotPassword.sending : t.auth.forgotPassword.submit}
          </Button>
        </FormComponent>
        <div className="text-center">
          <Link to="/" className="text-sm text-primary hover:underline">
            {t.auth.forgotPassword.backToLogin}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
